import { useState } from 'react';
import { generateIban } from '../ibanGenerator';
import { ibanFormats } from '../ibanFormats';
import { useEffect } from "react";

import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { countryNames } from '../countryNames';
import { countryFlags } from '../countryFlags';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from 'recharts';

const ENABLE_STATS = true; // 🔒 Disable Render stats
const BLOCK_IF_ADBLOCK = false; // 🔒 Set to true to block generate button when AdBlock is detected

function App() {
    const [country, setCountry] = useState('EE');
    const [iban, setIban] = useState(generateIban(''));
    const [isAdBlocked, setIsAdBlocked] = useState(false);
    const [hasCheckedAdBlock, setHasCheckedAdBlock] = useState(true);
    const [cooldown, setCooldown] = useState(false);
    const [stats, setStats] = useState([]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlCountry = params.get('country');

        if (urlCountry && ibanFormats[urlCountry]) {
            setCountry(urlCountry.toUpperCase());
            setIban(generateIban(urlCountry.toUpperCase()));
        } else {
            setIban(generateIban('EE')); // fallback to default
        }
    }, []);

    useEffect(() => {
        if (!BLOCK_IF_ADBLOCK) return;
        const testScript = document.createElement('script');
        testScript.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9513738842602458";
        testScript.async = true;
        testScript.setAttribute("data-adblock-test", "true");

        testScript.onerror = () => {
            setIsAdBlocked(true);
            setHasCheckedAdBlock(true);
        };

        testScript.onload = () => {
            setHasCheckedAdBlock(true); // ✅ set this on success too
        };

        document.body.appendChild(testScript);
    }, []);

    useEffect(() => {
        if (!ENABLE_STATS) return;

        const fetchStats = () => {
            fetch('https://www.randomiban.eu/api/stats.php')
                .then(res => res.json())
                .then(data => {
                    const enriched = data.map(stat => ({
                        ...stat,
                        countryName: countryNames[stat.country_code] || stat.country_code,
                    }));
                    setStats(enriched);
                })
                .catch(err => console.error('Failed to fetch stats', err));
        };

        fetchStats();
        const interval = setInterval(fetchStats, 10000);
        return () => clearInterval(interval);
    }, []);


    const regenerateIban = () => {
        console.log("Sending country to backend:", country);
        if (cooldown || (BLOCK_IF_ADBLOCK && isAdBlocked)) return;

        setIban(generateIban(country));

        if (ENABLE_STATS) {
            const payload = {
                countryCode: country,
                resolution: `${window.innerWidth}x${window.innerHeight}`,
                language: navigator.language,
                referrer: document.referrer || 'direct',
                version: '1.0.3'
            };

            fetch('https://www.randomiban.eu/api/log-generation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            }).catch(err => {
                console.error("Failed to log IBAN generation", err);
            });
        }

        setCooldown(true);
        setTimeout(() => setCooldown(false), 200); // 3-second cooldown
    };


    const handleCountryChange = (event) => {
        const newCountry = event.target.value;
        setCountry(newCountry);
        setIban(generateIban(newCountry));

        // Update URL param
        const url = new URL(window.location);
        url.searchParams.set('country', newCountry);
        window.history.replaceState({}, '', url);

        // Trigger logging immediately using newCountry (not old state)
        if (ENABLE_STATS) {
            fetch('https://www.randomiban.eu/api/log-generation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ countryCode: newCountry })
            }).catch(err => {
                console.error("Failed to log IBAN generation", err);
            });
        }
    };

    return (
        <>
            <Container maxWidth="sm" sx={{ textAlign: 'center', paddingTop: 6 }}>
                <FormControl fullWidth size="small" variant="outlined" sx={{ maxWidth: 200, marginX: 'auto', marginBottom: 2 }}>
                    <InputLabel>Country</InputLabel>

                    <Select value={country} label="Country" onChange={handleCountryChange}>
                        {Object.keys(ibanFormats).sort().map((code) => (
                            <MenuItem key={code} value={code}>
                                {countryNames[code]} {countryFlags[code]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Paper
                    elevation={0}
                    sx={{
                        padding: 2,
                        marginY: 2,
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                    }}
                >
                    <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
                        {!hasCheckedAdBlock
                            ? "Loading..."
                            : (BLOCK_IF_ADBLOCK && isAdBlocked)
                                ? "Please disable AdBlock"
                                : iban}
                    </Typography>

                </Paper>
                <Box>
                    <Button
                        variant="contained"
                        onClick={regenerateIban}
                        disabled={cooldown || (BLOCK_IF_ADBLOCK && isAdBlocked)}
                        sx={{
                            paddingX: 3,
                            paddingY: 1,
                            fontSize: '1rem',
                            borderRadius: '5px',
                            textTransform: 'none',
                        }}
                    >
                        {!hasCheckedAdBlock
                            ? "" // loading state
                            : (BLOCK_IF_ADBLOCK && isAdBlocked)
                                ? "Disable ad blocker and refresh the page to generate IBAN"
                                : cooldown
                                    ? "Please wait..." // during cooldown
                                    : "Generate new IBAN"}
                    </Button>
                    {ENABLE_STATS && (
                        <Box sx={{ mt: 6 }}>
                            <Typography variant="h6" gutterBottom>
                                IBAN Generation stats
                            </Typography>

                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={stats} layout="vertical" margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis type="number" />
                                    <YAxis type="category" dataKey="countryName" />
                                    <Tooltip formatter={(value) => [value, "Generated"]} />
                                    <Bar dataKey="count" fill="#1976d2" />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </Box>
            </Container>
        </>
    );
}

export default App;
